import React, { useState, useEffect } from 'react';
import { TextField, Checkbox, FormControlLabel, IconButton, Icon, Chip, Typography } from '@mui/material';
import { Button } from 'global/components/Button';
import makeStyles from '@mui/styles/makeStyles';
import { get, size } from 'global/lib/lodash';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

const AVAILABLE_SECTIONS = [
  { name: 'info', label: 'Info', defaultEnabled: true },
  { name: 'score_summary', label: 'Score Summary', defaultEnabled: true },
  { name: 'highlights', label: 'Highlights', defaultEnabled: true },
  { name: 'scores', label: 'Scores', defaultEnabled: true },
  { name: 'tasks', label: 'Tasks', defaultEnabled: false },
];

const TIER_OPTIONS = ['division', 'community', 'lot'];

const useStyles = makeStyles((theme) => ({
  container: {
    width: '100%',
  },
  optionCard: {
    border: '1px solid #ddd',
    borderRadius: 4,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    position: 'relative',
  },
  removeBtn: {
    position: 'absolute',
    top: 4,
    right: 4,
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
    paddingRight: 36,
  },
  tierRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  sectionsLabel: {
    fontWeight: 'bold',
    fontSize: 13,
    marginBottom: 4,
    color: theme.palette.text.secondary,
  },
  sectionRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '2px 0',
    borderRadius: 4,
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.03)',
    },
  },
  dragHandle: {
    cursor: 'grab',
    color: '#aaa',
    display: 'flex',
    alignItems: 'center',
    marginRight: 4,
  },
  orderNum: {
    fontSize: 12,
    color: '#999',
    minWidth: 20,
  },
  chipContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
    marginBottom: theme.spacing(1),
  },
}));

const ensureAllSections = (sections) => {
  const existing = Array.isArray(sections) ? sections : [];
  const maxOrder = existing.reduce((max, s) => Math.max(max, s.order || 0), 0);
  const result = [...existing];
  AVAILABLE_SECTIONS.forEach((as, i) => {
    if (!result.find((s) => s.name === as.name)) {
      result.push({ name: as.name, order: maxOrder + i + 1, enabled: as.defaultEnabled });
    }
  });
  return result;
};

const DEFAULT_OPTION = {
  name: 'Print Report',
  tier: [],
  sections: AVAILABLE_SECTIONS.map((s, i) => ({
    name: s.name,
    order: i + 1,
    enabled: s.defaultEnabled,
  })),
};

const PrintOptionsEditor = ({ value, onChange, disabled }) => {
  const classes = useStyles();
  const [options, setOptions] = useState(() => {
    if (Array.isArray(value) && size(value)) {
      return value.map((opt) => ({ ...opt, sections: ensureAllSections(opt.sections) }));
    }
    return [{ ...DEFAULT_OPTION }];
  });

  useEffect(() => {
    if (Array.isArray(value) && size(value)) {
      setOptions(value.map((opt) => ({ ...opt, sections: ensureAllSections(opt.sections) })));
    }
  }, [value]);

  const update = (newOptions) => {
    setOptions(newOptions);
    onChange && onChange(newOptions);
  };

  const updateOption = (index, key, val) => {
    const updated = [...options];
    updated[index] = { ...updated[index], [key]: val };
    update(updated);
  };

  const addOption = () => {
    update([...options, {
      name: '',
      tier: [],
      sections: AVAILABLE_SECTIONS.map((s, i) => ({
        name: s.name,
        order: i + 1,
        enabled: s.defaultEnabled,
      })),
    }]);
  };

  const removeOption = (index) => {
    const updated = options.filter((_, i) => i !== index);
    update(size(updated) ? updated : [{ ...DEFAULT_OPTION }]);
  };

  const toggleSectionEnabled = (optIndex, sectionName, enabled) => {
    const updated = [...options];
    const sections = [...get(updated[optIndex], 'sections', [])];
    const idx = sections.findIndex((s) => s.name === sectionName);
    if (idx >= 0) {
      sections[idx] = { ...sections[idx], enabled };
      updated[optIndex] = { ...updated[optIndex], sections };
      update(updated);
    }
  };

  const onDragEnd = (optIndex, result) => {
    if (!result.destination) return;

    const updated = [...options];
    const sections = [...get(updated[optIndex], 'sections', [])].sort((a, b) => a.order - b.order);

    const [moved] = sections.splice(result.source.index, 1);
    sections.splice(result.destination.index, 0, moved);

    const reordered = sections.map((s, i) => ({ ...s, order: i + 1 }));
    updated[optIndex] = { ...updated[optIndex], sections: reordered };
    update(updated);
  };

  const toggleTier = (optIndex, tier) => {
    const current = get(options[optIndex], 'tier', []);
    const has = current.includes(tier);
    const newTiers = has ? current.filter((t) => t !== tier) : [...current, tier];
    updateOption(optIndex, 'tier', newTiers);
  };

  // Name is only shown to users on the assessment page when multiple print
  // configs exist (single config → button just says "Print"), so don't let
  // users edit a name that won't be visible.
  const nameDisabled = disabled || size(options) <= 1;

  return (
    <div className={classes.container}>
      {options.map((opt, optIndex) => {
        const sorted = [...(get(opt, 'sections', []) || [])].sort((a, b) => a.order - b.order);
        return (
          <div key={optIndex} className={classes.optionCard}>
            {!disabled && size(options) > 1 && (
              <IconButton className={classes.removeBtn} size="small" onClick={() => removeOption(optIndex)}>
                <Icon>close</Icon>
              </IconButton>
            )}
            <div className={classes.cardHeader}>
              <TextField
                size="small"
                variant={nameDisabled ? 'standard' : 'outlined'}
                label="Name"
                value={get(opt, 'name', '')}
                onChange={(e) => updateOption(optIndex, 'name', e.target.value)}
                disabled={nameDisabled}
                helperText={!disabled && size(options) <= 1 ? 'Add another option to enable naming' : undefined}
                style={{ flex: 1 }}
              />
            </div>

            <div className={classes.tierRow}>
              <Typography variant="caption" style={{ fontWeight: 'bold', minWidth: 40 }}>Tier:</Typography>
              <div className={classes.chipContainer}>
                {!size(get(opt, 'tier', [])) && <Chip label="All" size="small" variant="outlined" />}
                {TIER_OPTIONS.map((tier) => {
                  const selected = (get(opt, 'tier', []) || []).includes(tier);
                  return (
                    <Chip
                      key={tier}
                      label={tier.charAt(0).toUpperCase() + tier.slice(1)}
                      size="small"
                      color={selected ? 'primary' : 'default'}
                      variant={selected ? 'filled' : 'outlined'}
                      onClick={disabled ? undefined : () => toggleTier(optIndex, tier)}
                      disabled={disabled}
                    />
                  );
                })}
              </div>
            </div>

            <div className={classes.sectionsLabel}>Sections:</div>
            {disabled ? (
              sorted.map((section, i) => {
                const label = get(AVAILABLE_SECTIONS.find((s) => s.name === section.name), 'label', section.name);
                return (
                  <div key={section.name} className={classes.sectionRow}>
                    <span className={classes.orderNum}>{i + 1}.</span>
                    <FormControlLabel
                      control={<Checkbox checked={section.enabled !== false} size="small" disabled />}
                      label={label}
                    />
                  </div>
                );
              })
            ) : (
              <DragDropContext onDragEnd={(result) => onDragEnd(optIndex, result)}>
                <Droppable droppableId={`sections-${optIndex}`}>
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                      {sorted.map((section, i) => {
                        const label = get(AVAILABLE_SECTIONS.find((s) => s.name === section.name), 'label', section.name);
                        return (
                          <Draggable key={section.name} draggableId={section.name} index={i}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className={classes.sectionRow}
                                style={{
                                  ...provided.draggableProps.style,
                                  backgroundColor: snapshot.isDragging ? 'rgba(25,118,210,0.08)' : undefined,
                                }}
                              >
                                <span {...provided.dragHandleProps} className={classes.dragHandle}>
                                  <Icon fontSize="small">drag_indicator</Icon>
                                </span>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={section.enabled !== false}
                                      onChange={(e) => toggleSectionEnabled(optIndex, section.name, e.target.checked)}
                                      size="small"
                                    />
                                  }
                                  label={label}
                                />
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </div>
        );
      })}
      {!disabled && (
        <Button variant="outlined" onClick={addOption} startIcon={<Icon>add</Icon>}>
          Add Print Option
        </Button>
      )}
    </div>
  );
};

export default PrintOptionsEditor;
